// apiInterceptor.js
import Axios from "axios";
import { Auth } from "aws-amplify";
import config from "../config.js";

import { triggerSnackbar } from "@/components/toastMessage";
import { accessToken, idToken, refreshToken } from "../store";
import { getRecoil, setRecoil } from "recoil-nexus";
import { hideLoader, showLoader } from "../contexts/loaderProvider.jsx";
import {
  emitSession401,
  emitTokenRefreshed,
  emitUserBlocked,
} from "./sessionEvents";

// ======================
// CONFIG
// ======================

const apiClient = Axios.create({
  baseURL: config.apiGateway.URL,
});

// ======================
// Refresh Pending Request Queue
// Holds in-flight requests that hit 401 while refresh is in progress.
// ======================
let isRefreshing = false;
let isLogoutScheduled = false;
const queue = [];

const markRequestAsRetried = (requestConfig, newToken) => {
  requestConfig.headers = requestConfig.headers || {};
  requestConfig.headers.Authorization = `Bearer ${newToken}`;
  requestConfig.meta = { ...requestConfig.meta, _isRetryAfterRefresh: true };
};

const getErrorText = (error) => {
  const parts = [
    error?.message,
    error?.code,
    error?.name,
    error?.response?.data?.message,
    error?.response?.data?.error,
    error?.response?.data?.errorMessage,
    error?.response?.data?.error_description,
  ]
    .filter(Boolean)
    .map((item) => String(item).toLowerCase());

  return parts.join(" ");
};

const isDisabledUserError = (error) => {
  const text = getErrorText(error);
  return text.includes("disabled") || text.includes("userdisabled");
};

const emitBlockedOnce = (message, delay = 0) => {
  if (isLogoutScheduled) return;
  isLogoutScheduled = true;

  const dispatch = () => {
    emitUserBlocked({ message });
  };

  if (delay > 0) {
    window.setTimeout(dispatch, delay);
    return;
  }

  dispatch();
};

export const performSilentRefresh = async () => {
  const storedRefreshToken = getRecoil(refreshToken);
  if (!storedRefreshToken) {
    const noRefreshTokenError = new Error("Missing refresh token");
    noRefreshTokenError.code = "missing_refresh_token";
    throw noRefreshTokenError;
  }

  const cognitoUser = await Auth.currentAuthenticatedUser();
  const { CognitoRefreshToken } = await import("amazon-cognito-identity-js");
  const cognitoRefreshToken = new CognitoRefreshToken({
    RefreshToken: storedRefreshToken,
  });

  const session = await new Promise((resolve, reject) => {
    cognitoUser.refreshSession(cognitoRefreshToken, (err, newSession) => {
      if (err) reject(err);
      else resolve(newSession);
    });
  });

  const newAccessToken = session.getAccessToken().getJwtToken();
  const newIdToken = session.getIdToken().getJwtToken();
  const refreshedToken = session.getRefreshToken().getToken();

  setRecoil(accessToken, newAccessToken);
  setRecoil(idToken, newIdToken);
  setRecoil(refreshToken, refreshedToken || storedRefreshToken);

  return newAccessToken;
};

export const pendingRequestQueue = {
  enqueue(resolve, reject, requestConfig) {
    queue.push({ resolve, reject, requestConfig });
  },
  flush(newToken, reason) {
    const items = queue.splice(0);
    isRefreshing = false;
    if (newToken) {
      isLogoutScheduled = false;
    }
    items.forEach(({ resolve, reject, requestConfig }) => {
      if (newToken) {
        markRequestAsRetried(requestConfig, newToken);
        resolve(apiClient(requestConfig));
      } else {
        reject(reason || new Error("Session expired"));
      }
    });
  },
  // Call on fresh login to clear any stale 401 state from the previous session.
  // Without this, queued requests from an expired session silently block all new API calls.
  reset() {
    const items = queue.splice(0);
    isRefreshing = false;
    isLogoutScheduled = false;
    items.forEach(({ reject }) => reject(new Error("Session expired")));
  },
};

// ======================
// Loader Request Counter
// ======================
let activeRequests = 0;
const startLoader = (text) => {
  if (activeRequests === 0) showLoader(text || "Loading...");
  activeRequests++;
};
const stopLoader = () => {
  activeRequests = Math.max(activeRequests - 1, 0);
  if (activeRequests === 0) hideLoader();
};

// ======================
// Retry Config
// ======================
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_RETRY_DELAY = 1000; // ms

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

// ======================
// REQUEST INTERCEPTOR
// ======================
apiClient.interceptors.request.use(
  (config) => {
    // Get the token dynamically for each request
    const token = getRecoil(accessToken);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Content-Type"] = "application/json";

    const { loader, silent } = config.meta || {};

    // Don't show loader if silent
    if (loader && !silent) {
      startLoader(config.meta?.loaderText);
    }

    // Initialize retry count per request
    config.meta = config.meta || {};
    config.meta.retryCount = config.meta.retryCount || 0;

    return config;
  },
  (error) => {
    stopLoader();
    return Promise.reject(error);
  },
);

// ======================
// RESPONSE INTERCEPTOR
// ======================
apiClient.interceptors.response.use(
  (response) => {
    const { loader, silent } = response.config.meta || {};
    if (loader && !silent) stopLoader();

    return response;
  },
  async (error) => {
    const config = error.config || {};
    const { loader, silent, retry } = config.meta || {};

    if (loader && !silent) stopLoader();

    // ----------------------
    // RETRY HANDLING
    // ----------------------
    const maxRetries = retry?.max ?? DEFAULT_MAX_RETRIES;
    const retryDelay = retry?.delay || DEFAULT_RETRY_DELAY;

    config.meta.retryCount = config.meta.retryCount || 0;

    const isNetworkError =
      !error.response &&
      (error.code === "ECONNABORTED" ||
        error.message === "Network Error" ||
        error.request);

    const isServerError =
      error.response && [500, 502, 503, 504].includes(error.response.status);

    const isRateLimitError = error.response?.status === 429;

    const shouldRetry =
      (isNetworkError || isServerError || isRateLimitError) &&
      config.meta.retryCount < maxRetries;

    if (shouldRetry) {
      config.meta.retryCount += 1;
      const backoff = Math.pow(2, config.meta.retryCount) * retryDelay;
      if (!silent) {
        triggerSnackbar(
          `Connection issue. Retrying... (attempt ${config.meta.retryCount} of ${maxRetries})`,
          "warning",
        );
      }
      await delay(backoff);
      return apiClient(config);
    }

    // ----------------------
    // ERROR HANDLING
    // ----------------------
    const customError = new Error(
      "An error occurred while processing your request.",
    );

    if (error.response) {
      customError.status = error.response.status;

      // Normalize response data keys to lowercase
      const rawData = error.response.data || {};
      const normalizedData = Object.fromEntries(
        Object.entries(rawData).map(([k, v]) => [k.toLowerCase(), v]),
      );

      const errorCode = String(normalizedData.errorcode || "").toLowerCase();
      const errorMessage =
        normalizedData.errormessage || "Something went wrong!";

      customError.code = errorCode;
      customError.message = errorMessage;

      // 4xx errors are user-facing (bad input, not found, forbidden, etc.)
      // 5xx errors are server-side and not meaningful to show to the user
      const isClientError =
        error.response.status >= 400 && error.response.status < 500;

      if (error.response.status === 401) {
        // If this is already a retried request (after session refresh), don't re-queue.
        // Reject immediately to avoid an infinite loop.
        if (error.config?.meta?._isRetryAfterRefresh) {
          return Promise.reject(customError);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            pendingRequestQueue.enqueue(resolve, reject, error.config);
          });
        }

        isRefreshing = true;

        try {
          const newToken = await performSilentRefresh();
          emitTokenRefreshed({ at: Date.now() });
          pendingRequestQueue.flush(newToken);

          markRequestAsRetried(config, newToken);
          return apiClient(config);
        } catch (refreshErr) {
          const refreshFailure = new Error("Unable to refresh session.");
          refreshFailure.status = 401;
          refreshFailure.code = refreshErr?.code || refreshErr?.name || "refresh_failed";
          refreshFailure.cause = refreshErr;

          pendingRequestQueue.flush(null, refreshFailure);

          const blockedMessage =
            "Your account has been blocked by an administrator.";

          if (isDisabledUserError(refreshErr)) {
            emitBlockedOnce(blockedMessage);
          } else if (refreshFailure.code === "missing_refresh_token") {
            emitSession401();
          } else {
            triggerSnackbar("Unable to refresh session.", "error");
            window.setTimeout(() => {
              emitSession401();
            }, 2000);
          }

          return Promise.reject(refreshFailure);
        } finally {
          isRefreshing = false;
        }
      }

      if (!silent && isClientError) {
        triggerSnackbar(errorMessage, "error");
      }
    } else {
      if (error.request) {
        customError.message = "No response received from server.";
      } else {
        customError.message = error.message;
      }

      if (!silent) {
        triggerSnackbar(customError.message, "error");
      }
    }

    return Promise.reject(customError);
  },
);

export default apiClient;
